/**
 * Keep recipe "ingredients" rows in sync with main_protein / main_vegetables.
 * Rows with _syncKey are managed from the main selectors; strip _syncKey before API submit.
 */
import { RECIPE_MAIN_PROTEINS, RECIPE_MAIN_VEGETABLES } from '../constants/communityRecipes';

export const PROTEIN_SYNC_PREFIX = 'protein:';
export const VEG_SYNC_PREFIX = 'veg:';

export function emptyIngredient() {
  return { name: '', quantity: '', unit: '', note: '' };
}

export function proteinLabelForSlug(slug) {
  const p = RECIPE_MAIN_PROTEINS.find((x) => x.value === slug);
  return p ? p.label : slug;
}

/**
 * @param {{ main_protein: string, main_vegetables: string[], ingredients: object[] }} params
 */
export function reconcileRecipeIngredients({ main_protein, main_vegetables, ingredients }) {
  const list = ingredients || [];
  const manual = list.filter((r) => !r._syncKey);
  const oldByKey = Object.fromEntries(list.filter((r) => r._syncKey).map((r) => [r._syncKey, r]));

  const syncRows = [];
  if (main_protein && main_protein !== 'none') {
    const k = `${PROTEIN_SYNC_PREFIX}${main_protein}`;
    const prev = oldByKey[k];
    syncRows.push({
      name: proteinLabelForSlug(main_protein),
      quantity: prev?.quantity ?? '',
      unit: prev?.unit ?? '',
      note: prev?.note ?? '',
      _syncKey: k,
    });
  }

  const vegSet = new Set(main_vegetables || []);
  for (const v of RECIPE_MAIN_VEGETABLES) {
    if (!vegSet.has(v.value)) continue;
    const k = `${VEG_SYNC_PREFIX}${v.value}`;
    const prev = oldByKey[k];
    syncRows.push({
      name: v.label,
      quantity: prev?.quantity ?? '',
      unit: prev?.unit ?? '',
      note: prev?.note ?? '',
      _syncKey: k,
    });
  }

  return [...syncRows, ...manual];
}

export function stripIngredientSyncKeys(rows) {
  return rows.map(({ _syncKey, ...rest }) => rest);
}

function normName(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function namesMatch(a, b) {
  return normName(a) === normName(b);
}

/**
 * Build ingredient rows with _syncKey for edit form from API recipe shape.
 * @param {{ main_protein?: string, main_vegetables?: string[], ingredients?: object[] }} recipe
 */
export function hydrateIngredientsForEdit(recipe) {
  const api = Array.isArray(recipe.ingredients)
    ? recipe.ingredients.map((r) => ({
        name: (r.name || '').trim(),
        quantity: (r.quantity || '').trim(),
        unit: (r.unit || '').trim(),
        note: (r.note || '').trim(),
      }))
    : [];
  const used = new Set();
  const out = [];

  const pushSync = (label, syncKey) => {
    const idx = api.findIndex((row, i) => !used.has(i) && namesMatch(row.name, label));
    let base = { name: label, quantity: '', unit: '', note: '' };
    if (idx >= 0) {
      used.add(idx);
      base = { ...api[idx], name: label };
    }
    out.push({ ...base, _syncKey: syncKey });
  };

  const mp = recipe.main_protein;
  if (mp && mp !== 'none') {
    pushSync(proteinLabelForSlug(mp), `${PROTEIN_SYNC_PREFIX}${mp}`);
  }
  const vegSet = new Set(recipe.main_vegetables || []);
  for (const v of RECIPE_MAIN_VEGETABLES) {
    if (!vegSet.has(v.value)) continue;
    pushSync(v.label, `${VEG_SYNC_PREFIX}${v.value}`);
  }
  api.forEach((row, i) => {
    if (used.has(i)) return;
    out.push({
      name: row.name,
      quantity: row.quantity,
      unit: row.unit,
      note: row.note,
    });
  });
  return out;
}
